import {
    AccessBayScheduling,
    AccessAutoMatching,
    AccessHaulerManagement,
    AccessPlantManagement,
    AccessProducerManagement,
    Dashboard,
    GetFluidScheduleDetails,
    GetMilkScheduleDetails,
    ListAdmins,
    ListBactoAlert,
    ListBaseTransfers,
    ListCIPWash,
    ListDrivers,
    ListDropoffs,
    ListHaulingCompanies,
    ListLabReports,
    ListManifests,
    ListMonthlyReports,
    ListPickups,
    ListPickupsAndLabs,
    ListProcessors,
    ListProducers,
    ListReceivers,
    ListRouteSessions,
    ListScheduleRoutes,
    ListSharedFiles,
    ListSilos,
    ListSubAdmins,
    ListSubHauling,
    ListSubProcessors,
    ListSubProducers,
    ListTrailers,
    ListUserGroups,
    ListUserGroupsV2,
    ManageAssignees,
    ManageAssignments,
    ManageCloseDay,
    ManageCustomFields,
    ManageDeferrals,
    ManageDeliveryReports,
    ManageEquity,
    ManageHaulingRates,
    ManageLinkedUsers,
    ManageMilkPrices,
    ManageProcessedFluidCommodities,
    ManageProducerPayReports,
    UploadDemurrageTimeOnPlant,
    UploadDemurrageJumpTruck,
    UploadDemurragePumpOffTime,
    UploadDemurrageDeliveryTime,
    UploadDemurrageDropoffAmount,
    UploadDownload,
    ViewAccountingReports,
    ViewContracts,
    ViewDocumentation,
    ViewDriverLocations,
    ViewMemberCenter,
    ViewReceivingDropoffs,
    ViewDriverPerformance,
    ViewScaleReadings,
    ViewSchedulingPerformance,
    ViewReporting,
    ViewCDFAReport,
    ViewCDFAMarketingReport,
    CanImpersonateUsers,
    ViewMilkPredictionReport,
    ViewContractPerformanceReport,
    ViewSupplyOutlookReport,
    ManageRapidTest,
    ManageHaulingContracts,
} from './permissions';
import LABELS from './constants';
import Split from '../../../split';
import { isSuperUser, getUser } from '../../../../utils';

const region = 'CDI';
const canAccessAutomatch = Split.getBooleanTreatment('can_access_schedule_automatch');

const SubAdminManagement = {
    label: LABELS.Management,
    permissions: [
        ListAdmins,
        ListProducers,
        ListSubProducers,
        ListDrivers,
        ListProcessors(region),
        ListSubProcessors(region),
        ListHaulingCompanies(region),
        ListSubHauling(region),
        ListTrailers,
        ListSubAdmins,
        ManageLinkedUsers,
        CanImpersonateUsers,
        // force wrap
    ],
};

const SubAdminGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard,
        ListSilos,
        ListUserGroups,
        ListUserGroupsV2,
        ViewDriverLocations,
        ListScheduleRoutes,
        ManageCloseDay,
        ListPickups(),
        ListDropoffs(),
        ListCIPWash,
        ListLabReports(),
        ManageEquity,
        UploadDownload,
        ListRouteSessions(region),
        ManageDeliveryReports,
        ListManifests,
        GetMilkScheduleDetails(),
        GetFluidScheduleDetails,
        ViewReceivingDropoffs,
        ListSharedFiles,
        ManageProcessedFluidCommodities,
        ManageCloseDay,
        ViewDocumentation,
        ViewMemberCenter,
        AccessPlantManagement,
        AccessHaulerManagement,
        AccessProducerManagement,
        ...(canAccessAutomatch ? [AccessAutoMatching] : []),
        AccessBayScheduling,
        ViewContracts,
        // force-wrap
        ManageCustomFields,
        ViewDriverPerformance,
        ViewSchedulingPerformance,
        ViewScaleReadings,
        ViewReporting,
        ViewCDFAReport,
        ViewCDFAMarketingReport,
        ViewMilkPredictionReport,
        ViewContractPerformanceReport,
        ViewSupplyOutlookReport,
        ManageRapidTest,
    ],
};

const SubAdminAccounting = {
    label: LABELS.Accounting,
    permissions: [
        ViewAccountingReports,
        ManageAssignments,
        ManageAssignees,
        ManageMilkPrices,
        ManageHaulingRates,
        ManageProducerPayReports,
        ListBaseTransfers(region),
        ManageDeferrals,
        ManageHaulingContracts(),
        // force wrap
    ],
};

const SubProducerManagement = {
    label: LABELS.Management,
    permissions: [ListSubProducers],
};

const SubProducerGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard,
        ListPickups('View', ''),
        ListPickupsAndLabs,
        ListLabReports('View', ''),
        ListBactoAlert(region),
        UploadDownload,
        ListSharedFiles,
        ViewMemberCenter,
        AccessProducerManagement,
        ViewReporting,
        // force wrap
    ],
};

const SubProducerAccounting = {
    label: LABELS.Accounting,
    permissions: [ListMonthlyReports(region)],
};

const SubProcessorManagement = {
    label: LABELS.Management,
    permissions: [ListReceivers, ListSubProcessors(region)],
};

const SubProcessorGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard,
        ListSilos,
        ListPickups('View', ''),
        ListDropoffs('View', ''),
        ListRouteSessions(region, 'View', ''),
        GetMilkScheduleDetails('Manage', '(View, Create Demand)'),
        GetFluidScheduleDetails,
        ViewReceivingDropoffs,
        ListSharedFiles,
        AccessPlantManagement,
        AccessBayScheduling,
        ViewReporting,
        // force wrap
    ],
};

const SubHaulingManagement = {
    label: LABELS.Management,
    permissions: [ListDrivers, ListTrailers, ListSubHauling(region)],
};

const SubHaulingGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard,
        ViewDriverLocations,
        ListPickups(),
        ListDropoffs(),
        ListRouteSessions(region, 'View', ''),
        ListCIPWash,
        GetMilkScheduleDetails('View', ''),
        GetFluidScheduleDetails,
        ViewReceivingDropoffs,
        ListSharedFiles,
        AccessHaulerManagement,
        ViewReporting,
        ViewDriverPerformance,
        // force wrap
    ],
};

const AdminDemurragePermissions = {
    label: LABELS.Demurrage,
    permissions: [
        UploadDemurrageTimeOnPlant(),
        UploadDemurrageJumpTruck(),
        UploadDemurragePumpOffTime(),
        UploadDemurrageDeliveryTime(),
        UploadDemurrageDropoffAmount(),
        // force wrap
    ],
};

const canMutateProcessorDemurragePermissions = () => {
    const user = getUser();
    const superUser = isSuperUser();
    const isAdmin = ['admin'].includes(user.role) && !user.subuser;
    return isAdmin || superUser;
};

const ProcessorDemurragePermissions = {
    label: LABELS.Demurrage,
    permissions: [
        UploadDemurrageDeliveryTime({ enableMutationFn: canMutateProcessorDemurragePermissions }),
        UploadDemurrageDropoffAmount({ enableMutationFn: canMutateProcessorDemurragePermissions }),
        // force wrap
    ],
};

const SubAdmin = [SubAdminManagement, SubAdminGeneral, SubAdminAccounting, AdminDemurragePermissions];
const SubProducer = [SubProducerManagement, SubProducerGeneral, SubProducerAccounting];
const SubProcessor = [SubProcessorManagement, SubProcessorGeneral, ProcessorDemurragePermissions];
const SubHauling = [SubHaulingManagement, SubHaulingGeneral];

const REQUIRED_PERMISSIONS = {
    Admin: [
        ListSilos,
        ListUserGroups,
        ViewReceivingDropoffs,
        ListManifests,
        GetMilkScheduleDetails(),
        GetFluidScheduleDetails,
        ListTrailers,
        // force-wrap
    ],
};

const CDI = {
    Admin: { permissions: SubAdmin },
    'Sub-Admin': { permissions: SubAdmin },
    'Sub-Producer': { permissions: SubProducer },
    Accountant: { permissions: SubProducer },
    'Feed-Provider': { permissions: SubProducer },
    Nutritionist: { permissions: SubProducer },
    'Co-Owner': { permissions: SubProducer },
    Processor: { permissions: SubProcessor },
    'Sub-Processor': { permissions: SubProcessor },
    'Sub-Hauling': { permissions: SubHauling },
};

export default CDI;
